import type { Site, DailySessionsData } from '@hotjar/site';
import { useDailySessions } from './useDailySessions';

const SINGLE_SITE_DAILY_TRAFFIC_THRESHOLD = 4000;
const MULTI_SITE_MINIMUM_SITES_COUNT = 4;
const MULTI_SITE_DAILY_TRAFFIC_THRESHOLD = 500;

interface UseQualifyingSalesLeadProps {
  sites: Array<Site> | undefined;
}

/**
 * This hook checks if the current site customer is a Qualifying Sales Lead (also known as high potential customer or high-value customer)
 * @returns a boolean representing if the current site customer is a qualifying sales lead
 */
export const useQualifyingSalesLead = (props: UseQualifyingSalesLeadProps): boolean => {
  const sitesDailyTraffic = useDailySessions(props.sites);

  // first, let's see if account has any site with traffic more than SINGLE_SITE_DAILY_TRAFFIC_THRESHOLD
  let isQualifyingSalesLead = sitesDailyTraffic?.some(
    (siteDailyTraffic) =>
      (siteDailyTraffic.data as DailySessionsData)?.sessions > SINGLE_SITE_DAILY_TRAFFIC_THRESHOLD,
  );

  // if user is still not qualified, let's see if account has more than MULTI_SITE_MINIMUM_SITES_COUNT sites with traffic more than MULTI_SITE_DAILY_TRAFFIC_THRESHOLD
  if (!isQualifyingSalesLead) {
    // number of sites with daily traffic above MULTI_SITE_DAILY_TRAFFIC_THRESHOLD
    const sitesAboveThreshold = sitesDailyTraffic?.filter(
      (siteDailyTraffic) =>
        (siteDailyTraffic.data as DailySessionsData)?.sessions > MULTI_SITE_DAILY_TRAFFIC_THRESHOLD,
    ).length;

    isQualifyingSalesLead = sitesAboveThreshold >= MULTI_SITE_MINIMUM_SITES_COUNT;
  }

  return isQualifyingSalesLead;
};
